import type { ReactNode } from "react";
import { cn, type Drug, type UseDrugSearchReturn } from "@dearbill/shared";

interface DrugResultsListboxProps {
  id: string;
  search: UseDrugSearchReturn;
  /** Rxcuis already on the list: their rows are `aria-disabled`, tagged "Added" and ignore clicks. */
  listed: ReadonlySet<string>;
  /** The keyboard- or hover-active row; -1 for none. */
  activeIndex: number;
  onPick: (drug: Drug) => void;
  onHover: (index: number) => void;
}

/**
 * The add bar's results listbox: one `option` per catalog match (name and
 * label, no prices), or the searching, error and no-match notices. Rendered
 * inside the input's `relative` wrapper, under the field. An option's DOM id
 * is `{id}-{rxcui}`, which the combobox's `aria-activedescendant` points at.
 * `onMouseDown` prevents the input's blur so the click's pick lands.
 */
export function DrugResultsListbox({
  id,
  search,
  listed,
  activeIndex,
  onPick,
  onHover,
}: DrugResultsListboxProps): ReactNode {
  let rows: ReactNode;
  if (search.loading) rows = <Notice>Searching…</Notice>;
  else if (search.error)
    rows = <Notice>{"Search isn't working right now — please try again."}</Notice>;
  else if (search.results.length === 0) rows = <Notice>No matches</Notice>;
  else
    rows = search.results.map(({ drug }, i) => {
      const added = listed.has(drug.rxcui);
      return (
        <li
          key={drug.rxcui}
          id={`${id}-${drug.rxcui}`}
          role="option"
          aria-selected={i === activeIndex}
          aria-disabled={added || undefined}
          className={cn(
            "text-body-sm flex items-center justify-between gap-4 px-4 py-2",
            added ? "opacity-60" : "hover:bg-ice cursor-pointer",
            i === activeIndex && "bg-ice",
          )}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onPick(drug)}
          onMouseEnter={() => onHover(i)}
        >
          <span className="min-w-0">
            <span className="text-midnight font-semibold">{drug.drugName}</span>
            {drug.label.trim() !== "" && <span className="text-slate-teal"> {drug.label}</span>}
          </span>
          {added && (
            <span className="text-eyebrow-sm text-slate-teal shrink-0 uppercase">Added</span>
          )}
        </li>
      );
    });
  return (
    <ul
      id={id}
      role="listbox"
      aria-label="Matching medications"
      className="border-haze absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-auto rounded-2xl border bg-white py-2 shadow-lg"
    >
      {rows}
    </ul>
  );
}

function Notice({ children }: { children: ReactNode }): ReactNode {
  return <li className="text-body-sm text-slate-teal px-4 py-2">{children}</li>;
}
